import sop from "@/images/icons/sop.svg";

import {boundMethod} from "autobind-decorator";
import {parseISO} from "date-fns";
import {motion} from "framer-motion";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";
import {Link, RouteComponentProps} from "react-router-dom";

import {EModules} from "@/models";
import {animation, onClosed, onOpen} from "@toolbox/design/models";
import {EDeviceClasses} from "@toolbox/models";
import {ETemplateTab, ITemplateModel} from "./models";

import Tooltip from "@toolbox/building-blocks/Tooltip";
import Time from "@toolbox/design/Time";
import ContainerActivity from "@toolbox/display-blocks/ContainerActivity";
import ContainerCol from "@toolbox/display-blocks/ContainerCol";
import Highlighter from "@toolbox/display-blocks/Highlighter";
import ModuleIcon, {getModuleInfo} from "@toolbox/display-blocks/ModuleIcon";
import {getDeviceDisplayName} from "@toolbox/functions/device-check";
import T from "@translate/T";
import TemplateAudit from "./TemplateAudit";
import TemplateSearch from "./TemplateSearch";
import TemplateTaskPanel from "./TemplateTaskPanel";

interface ITemplateListProps extends RouteComponentProps {
    intl: IntlShape;
    baseUrl: string;
    templateTab: ETemplateTab;

    onChangeTab(templateTab: ETemplateTab): Promise<void>;
}

interface ITemplateListState {
    templates: ITemplateModel[];
    query: string;
    templateTab: ETemplateTab;
}

function getTabTitle(templateTab: ETemplateTab): JSX.Element {
    switch (templateTab) {
        case ETemplateTab.Analysis:
            return <T>Analysis templates</T>;

        case ETemplateTab.Sop:
            return <T>SOP templates</T>;
    }
}

class TemplateList extends React.PureComponent<
    ITemplateListProps,
    ITemplateListState
> {
    public readonly state: ITemplateListState = {
        templates: [],
        query: "",
        templateTab: this.props.templateTab,
    };

    public componentDidUpdate(prevProps: Readonly<ITemplateListProps>) {
        const {templateTab} = this.props;
        if (prevProps.templateTab !== templateTab) {
            this.setState({templateTab});
        }
    }

    @boundMethod
    public onChange(templates: ITemplateModel[], query: string) {
        this.setState({templates, query});
    }

    @boundMethod
    public onClick(e: React.SyntheticEvent<HTMLAnchorElement>) {
        const {baseUrl, history, onChangeTab} = this.props;
        e.preventDefault();

        const id = e.currentTarget.dataset.tab as ETemplateTab;
        const [_, front] = baseUrl.split("/");
        history.push(`/${front}/${id}`);
        onChangeTab(id);
    }

    public render() {
        return (
            <ContainerCol>
                <ContainerActivity
                    left={this.renderLeft()}
                    right={this.renderRight()}
                />
            </ContainerCol>
        );
    }

    private renderLeft() {
        const {baseUrl, history, location, match} = this.props;
        const {templateTab} = this.state;
        const dataTabs = Object.values(ETemplateTab);

        return (
            <React.Fragment>
                <TemplateTaskPanel baseUrl={baseUrl} />

                <h3 id="analysis-templates-page">
                    <T>Templates</T>
                </h3>
                <ul className="nav nav-tabs mt-1 mb-2" role="tablist">
                    <div className="tab-height" />
                    {this.renderTabs(dataTabs)}
                    <hr />
                </ul>

                <TemplateSearch
                    templateTab={templateTab}
                    history={history}
                    location={location}
                    match={match}
                    onChange={this.onChange}
                >
                    {this.renderTemplates()}
                </TemplateSearch>
            </React.Fragment>
        );
    }

    private renderTabs(templateTabs: ETemplateTab[]) {
        const {templateTab} = this.state;

        return templateTabs.map((tab, i) =>
            this.renderTab(tab, templateTab === tab),
        );
    }

    private renderTab(templateTab: ETemplateTab, selected: boolean) {
        return (
            <li key={templateTab} className="nav-item tab">
                <a
                    id={"data-ranged-tab-" + templateTab}
                    className={"nav-link" + (selected ? " active" : "")}
                    aria-controls={"data-ranged-content-" + templateTab}
                    aria-selected={selected}
                    data-tab={templateTab}
                    data-toggle="tab"
                    href={"#data-ranged-content-" + templateTab}
                    role="tab"
                    onClick={this.onClick}
                >
                    <span className="nav-span">
                        <Tooltip>{getTabTitle(templateTab)}</Tooltip>
                    </span>
                </a>
            </li>
        );
    }

    private renderRight() {
        return <TemplateAudit baseUrl={this.props.baseUrl} />;
    }

    @boundMethod
    private renderTemplates() {
        const {templateTab} = this.state;

        return templateTab === ETemplateTab.Analysis
            ? this.renderAnalysisTemplates()
            : this.renderSopTemplates();
    }

    private renderAnalysisTemplates() {
        const {baseUrl, intl} = this.props;
        const {templates, query} = this.state;

        return templates.map((template) => {
            const info = getModuleInfo(template.engine ?? EModules.LumView);

            return (
                <motion.tr
                    key={template.id}
                    animate={onOpen}
                    exit={onClosed}
                    initial={onClosed}
                    transition={animation}
                >
                    <td>
                        <Link
                            id={"templates-link-" + template.id}
                            title={info.type(intl)}
                            to={`${baseUrl}/edit/${template.id}`}
                        >
                            <ModuleIcon
                                icon={info.icon}
                                name={template.name}
                                highlighter={query}
                            />
                        </Link>
                    </td>
                    <td id={"templates-engine-" + template.id}>
                        <Highlighter query={query} text={info.type(intl)} />
                    </td>
                    <td id={"templates-modified-" + template.id}>
                        <Time value={parseISO(template.modified)} />
                    </td>
                </motion.tr>
            );
        });
    }

    private renderSopTemplates() {
        const {baseUrl} = this.props;
        const {templates, query} = this.state;

        return templates.map((template) => {
            const device = getDeviceDisplayName(
                template.deviceClass ?? EDeviceClasses.Reader,
            );

            return (
                <motion.tr
                    key={template.id}
                    animate={onOpen}
                    exit={onClosed}
                    initial={onClosed}
                    transition={animation}
                >
                    <td>
                        <Link
                            id={"templates-link-" + template.id}
                            title={device}
                            to={`${baseUrl}/edit/${template.id}`}
                        >
                            <ModuleIcon
                                icon={sop}
                                name={template.name}
                                highlighter={query}
                            />
                        </Link>
                    </td>
                    <td id={"templates-device-" + template.id}>
                        <Highlighter query={query} text={device} />
                    </td>
                    <td id={"templates-modified-" + template.id}>
                        <Time value={parseISO(template.modified)} />
                    </td>
                </motion.tr>
            );
        });
    }
}

export default injectIntl(TemplateList);
